import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { SortablePhoto } from '../types/apartment';
import { SortablePhotoThumbnail } from './SortablePhotoThumbnail';

interface PhotoCarouselProps {
  photos: SortablePhoto[];
  activeId: string | null;
  isDragging: boolean;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
  onMarkAsPrimary: (key: string) => void;
  onDeletePhoto: (key: string) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  leftArrowRef: React.RefObject<HTMLButtonElement>;
  rightArrowRef: React.RefObject<HTMLButtonElement>;
}

/**
 * Horizontal sortable thumbnail carousel backed by @dnd-kit.
 *
 * Arrow button disabled state is derived from scroll position:
 *  - Left arrow: disabled when scrollLeft === 0
 *  - Right arrow: disabled when scrollLeft >= scrollWidth - clientWidth
 *
 * autoScroll is disabled on DndContext — useArrowAutoScroll handles all scrolling
 * via pointermove hit-testing against the arrow buttons.
 */
export function PhotoCarousel({
  photos,
  activeId,
  isDragging,
  onDragStart,
  onDragEnd,
  onDragCancel,
  onMarkAsPrimary,
  onDeletePhoto,
  scrollContainerRef,
  leftArrowRef,
  rightArrowRef,
}: PhotoCarouselProps) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Track a ref to canScrollRight for use in the effect without re-subscribing
  const canScrollRightRef = useRef(canScrollRight);
  canScrollRightRef.current = canScrollRight;

  const updateArrowState = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    updateArrowState();
    el.addEventListener('scroll', updateArrowState, { passive: true });
    return () => el.removeEventListener('scroll', updateArrowState);
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 5 px of movement before activating drag — prevents clicks from starting drags.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activePhoto = activeId !== null ? photos.find(p => p.key === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      autoScroll={false}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className="relative flex items-center gap-1">
        {/* Left arrow */}
        <button
          ref={leftArrowRef}
          type="button"
          aria-label="Scroll carousel left"
          disabled={!canScrollLeft}
          className={`flex-shrink-0 p-1 rounded-full border transition-all ${
            canScrollLeft
              ? isDragging
                ? 'bg-blue-100 border-blue-400 text-blue-700 scale-110'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              : 'bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          onClick={() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollLeft -= 120;
            }
          }}
        >
          <ChevronLeft size={16} />
        </button>

        {/* Scrollable thumbnail strip */}
        <div
          ref={scrollContainerRef}
          className="flex gap-2 overflow-x-hidden scroll-smooth"
          style={{ scrollbarWidth: 'none' }}
        >
          <SortableContext items={photos.map(p => p.key)} strategy={horizontalListSortingStrategy}>
            {photos.map(photo => (
              <div key={photo.key} className="group">
                <SortablePhotoThumbnail
                  photo={photo}
                  isPrimary={photo.displayOrder === 0}
                  onMarkAsPrimary={() => onMarkAsPrimary(photo.key)}
                  onDelete={() => onDeletePhoto(photo.key)}
                />
              </div>
            ))}
          </SortableContext>
        </div>

        {/* Right arrow */}
        <button
          ref={rightArrowRef}
          type="button"
          aria-label="Scroll carousel right"
          disabled={!canScrollRight}
          className={`flex-shrink-0 p-1 rounded-full border transition-all ${
            canScrollRight
              ? isDragging
                ? 'bg-blue-100 border-blue-400 text-blue-700 scale-110'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              : 'bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          onClick={() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollLeft += 120;
            }
          }}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* DragOverlay renders the ghost outside the scroll container's overflow clip */}
      <DragOverlay>
        {activePhoto !== null && activePhoto !== undefined ? (
          <SortablePhotoThumbnail
            photo={activePhoto}
            isPrimary={activePhoto.displayOrder === 0}
            onMarkAsPrimary={() => undefined}
            onDelete={() => undefined}
            isOverlay
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
